/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory } from 'react-router-dom';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import { Tooltip } from 'antd';
// local imports
import { useStyles } from '~src/hooks/useStyles';
import { idOrNull } from '~src/services/api';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import {
  dbFileToTableEntry,
  sortByModified,
  sortByType,
  TableEntry,
} from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { VersionSelect } from '~components/_common/VersionSelect/VersionSelect';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { styleFn } from './FileTable.style';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import { DbFocusOptions } from '~src/types/db';

import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { uniq } from 'lodash';
import {
  FileViewerMargins,
  useFileViewerContext,
} from '~src/contexts/FileToViewContext';
import { useSortByRevision } from '~src/hooks/utils/useSortByRevision';
import { formatDate } from '~src/utils/formatDate';
import _ from 'lodash';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useTenderFoldersFolders } from '~src/hooks/api-hooks/useTenderFoldersFolders';
import { dbTenderToTableEntry } from './TenderDocumentsTable';
import FolderTender from '~src/assets/nav-icons/folder-tender.svg';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';
import { useProjectId } from '~src/hooks/useProjectId';
import { getIsFilePreviewableByIcon } from './FileTable';
import { SVFIconType } from '~components/atoms/Viewers/SVFViewer/components/SVFIconType';

export interface TenderUnassignedFoldersProps extends TableProps<TableEntry> {
  selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
  onSelect?: TableRowSelection<TableEntry>['onSelect'];
  onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
  highlightedRow?: TableEntry;
  suMode?: boolean;
  setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
  handleRow: Required<TenderUnassignedFoldersProps>['onRow'];
  onEmptyOverlay?: () => void;
  focusOptions?: DbFocusOptions[];
}
/** Default TenderUnassignedFolders, used with folder navigation */
export const TenderUnassignedFolders: React.FC<TenderUnassignedFoldersProps> = ({
  selectedKeys,
  onSelect,
  onSelectAll,
  highlightedRow,
  suMode,
  handleRow,
  setSelectedItems,
  onEmptyOverlay,
  focusOptions,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const currentFolder = useCurrentFolder();
  const folderId = currentFolder?.id || null;
  const permissionLevel = currentFolder?.permission || 0;
  const projectId = useProjectId();
  const history = useHistory();
  const archiveSettings = useArchiveSettings();
  const isTagsOn = archiveSettings?.data?.resArchiveSettings?.is_tags_on;
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const {
    fileToView,
    setFileToView,
    setViewerTopMargin,
  } = useFileViewerContext();
  const filesQueryBaseKeys = {
    folderId: idOrNull(folderId),
    showDeleted: suMode,
    withVersions: true,
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
  };
  const [lastKnownData, setLastKnownData] = useState<any>();

  const { sortByRevision } = useSortByRevision();
  const foldersQueryBaseKeys = {
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  };
  const pathSegments = history.location.pathname.split('/').filter(Boolean);
  const root_folder_id = pathSegments[3];
  const current_folder_id = pathSegments[5];
  const lastSegment = pathSegments[pathSegments.length - 1];
  const isLastSegmentNan = !isNaN(Number(lastSegment));
  const foldersQuery = useTenderFoldersFolders(
    {
      root_folder_id,
    }
    //{ enabled: penultimateSegment === lastSegment }
  );

  const filesQuery = useFiles(filesQueryBaseKeys, {
    enabled: !!currentFolder && permissionLevel > 1,
    onError: () =>
      // go to /archive
      history.replace(`/projects/${projectId}/archive`),
  });

  useEffect(() => {
    if (foldersQuery.data !== undefined) {
      setLastKnownData(foldersQuery);
    } else if (!isLastSegmentNan) {
      setLastKnownData(undefined);
    }
  }, [foldersQuery.data]);

  const searchContext = useContext(SearchContext);

  const foldersSource = useMemo(
    () =>
      (lastKnownData?.data || [])
        .filter((v) => {
          return v.parent_id === Number(current_folder_id);
        })
        .map((folder) =>
          dbTenderToTableEntry(folder, Number(root_folder_id), true)
        ),
    [lastKnownData?.data, current_folder_id, root_folder_id]
  );

  const filesSource = useMemo(
    () =>
      (filesQuery.data || [])
        .filter((item) => item.state !== DB_STATE.PARTIAL)
        .map((item) => dbFileToTableEntry(item)),
    [filesQuery.data]
  );

  const filteredFolders = [
    ...foldersSource.filter((item) => {
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          item.dbFile?.versions &&
            item.dbFile?.versions[0] &&
            item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const filteredFiles = [
    ...filesSource.filter((item) => {
      //getting index of version in [versions] array by checking fileVersionsContext and [all current versions] array
      //Array.prototype.findIndex() || gets the index of the first element in the array that satisfies the provided testing function
      const dynamicIndex = (item.dbFile?.versions || []).findIndex(
        (el) =>
          //provided testing function
          el.version === fileVersionsContext[item.id]?.version &&
          fileVersionsContext[item.id]?.version
      );
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          dynamicIndex > 0
            ? item.dbFile?.versions &&
                item.dbFile?.versions[dynamicIndex] &&
                item.dbFile?.versions[dynamicIndex].version
            : item.dbFile?.versions &&
                item.dbFile?.versions[0] &&
                item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const getBackgroundRow = (record: TableEntry) => {
    const data =
      record.dbFolder && record.dbFolder.focus
        ? _.find(focusOptions, {
            value: record.dbFolder.focus,
          })
        : '';

    if (
      (record.key === highlightedRow?.key && data) ||
      record.disabled ||
      window.sessionStorage.getItem('appState') === 'timeMachine'
    )
      return {
        color: 'black',
        background: `${data ? '#' + data.color_hex_code_opacity : ''}`,
      };
    return {
      color: 'black',
      background: `${data ? '#' + data.color_hex_code : ''}`,
    };
  };

  const toPreviewableDbFile = (dbFile: any, projectId: number) => {
    if (!dbFile) return undefined;

    let versionsArr: any[] = [];

    // if versions exist, use them
    if (Array.isArray(dbFile.versions) && dbFile.versions.length) {
      versionsArr = [...dbFile.versions];
    } else {
      // if no versions, fallback to files (for backward compatibility with old API response)
      const files = Array.isArray(dbFile.files) ? dbFile.files : [];
      versionsArr = files.map((f: any) => ({
        ...f,
        project_id: f.project_id ?? projectId,
        versioned_file_id: f.versioned_file_id ?? dbFile.id,
        location: f.location ?? '',
        version: f.serial_number ?? f.version ?? f.revision,
        id:
          f.file_instance_id || f.file_id || f.registration_instance_id || f.id,
      }));
    }

    // sort latest by serial_number.
    // if serial_number is undefined, fallback to created_at.
    versionsArr.sort((a, b) => {
      const valA = a.serial_number ?? a.created_at ?? 0;
      const valB = b.serial_number ?? b.created_at ?? 0;
      return valB - valA;
    });

    return {
      ...dbFile,
      project_id: dbFile.project_id ?? projectId,
      folder_id: dbFile.folder_id ?? undefined,
      versions: versionsArr,
    };
  };

  // SVF Viewer
  const getCurrentFileVersion = (record: TableEntry) => {
    if (!record.dbFile) return undefined;

    return (
      fileVersionsContext[record.id] ||
      record.dbFile?.versions?.find(
        (version: any) => version.id === fileVersionsContext[record.id]?.id
      ) ||
      record.dbFile?.files?.find(
        (file: any) =>
          (file.registration_instance_id || file.file_id || file.id) ===
          fileVersionsContext[record.id]?.id
      ) ||
      record.dbFile?.versions?.[record.dbFile.versions.length - 1] ||
      record.dbFile?.versions?.[0] ||
      record.dbFile?.files?.[record.dbFile.files.length - 1] ||
      record.dbFile?.files?.[0]
    );
  };

  const getFileSVFVersion = (record: TableEntry) => {
    const currentVersion = getCurrentFileVersion(record);

    if (!currentVersion) return undefined;

    return {
      ...currentVersion,
      name: currentVersion.name || record.name || record.dbFile?.title || '',
      version:
        currentVersion.serial_number ??
        currentVersion.version ??
        currentVersion.revision ??
        1,
      revision:
        currentVersion.serial_number ??
        currentVersion.version ??
        currentVersion.revision ??
        1,
      updated_at: currentVersion.updated_at || record.updatedAt,
      description:
        currentVersion.registration_instance_description ??
        currentVersion.description ??
        record.dbFile?.description ??
        '',
      registration_instance_description:
        currentVersion.registration_instance_description ?? '',
      is_svf_convertible:
        currentVersion.is_svf_convertible ??
        record.dbFile?.versions?.[0]?.is_svf_convertible ??
        false,
      extension_svf_model:
        currentVersion.extension_svf_model ??
        record.dbFile?.versions?.[0]?.extension_svf_model ??
        null,
    };
  };

  const getFileSVFState = (record: TableEntry) =>
    getFileSVFVersion(record)?.extension_svf_model?.state;

  const getUnassignedTenderSVFFile = (record: TableEntry) => {
    if (!record.dbFile) return undefined;

    const currentVersion = getFileSVFVersion(record);

    if (!currentVersion || currentVersion.extension_svf_model?.state !== 7) {
      return undefined;
    }

    return {
      ...record.dbFile,
      sourceType: 'tender-unassigned-inside-svf',
      root_folder_id: Number(root_folder_id),
      folder_id: Number(current_folder_id || root_folder_id),
      versioned_file_id: Number(
        currentVersion.registration_id ||
          currentVersion.versioned_file_id ||
          record.dbFile.id
      ),
      file_guid: String(
        currentVersion.file_id || currentVersion.file_guid || currentVersion.id
      ),
      title: currentVersion.name || record.dbFile.title || record.name || '',
      file_name:
        currentVersion.name || record.dbFile.title || record.name || '',
      extension_svf_model: currentVersion.extension_svf_model || null,
      is_svf_convertible: currentVersion.is_svf_convertible === true,
      serial_number:
        currentVersion.serial_number ??
        currentVersion.version ??
        currentVersion.revision ??
        1,
      updated_at: currentVersion.updated_at || record.updatedAt,
      description:
        currentVersion.registration_instance_description ??
        currentVersion.description ??
        record.dbFile.description ??
        '',
    };
  };

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: '',
        width: 30,
        align: 'center',
        dataIndex: 'action',
        render: (type, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <a
                className="ant-dropdown-link"
                onClick={(e) => {
                  e.preventDefault();
                  const customEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(customEvent);
                }}
                css={styles.dotAnchor}>
                <IconButton
                  iconStyle="fal"
                  icon="ellipsis-v"
                  customStyle={styles.dotIcon}
                  color={'darkGray'}
                  tooltip={intl.formatMessage({ id: 'actions' })}
                />
              </a>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 60,
        align: 'center',
        render: (type, record) => {
          const isTenderFolder = record.folder;
          const isPreviewableFormat = getIsFilePreviewableByIcon(type.icon);
          const ext = record.extension?.toLowerCase() || '';
          const isCadFile = ['rvt', 'dwg', 'ifc'].includes(ext);

          const fileVersion = getFileSVFVersion(record);
          const fileState = getFileSVFState(record);

          const canShowSVFActionIcon =
            !isTenderFolder &&
            !!record.dbFile &&
            !!fileVersion?.extension_svf_model &&
            Number(fileState) === 7;

          const canOpenSVFViewer = fileState === 7;

          const cadDisplayVersion = isCadFile
            ? ({
                ...(fileVersion || {}),
                name: fileVersion?.name || record.name || '',
                extension_svf_model: fileVersion?.extension_svf_model ?? null,
                is_svf_convertible: fileVersion?.is_svf_convertible === true,
              } as any)
            : null;

          const handlePreviewClick = (e: any) => {
            e.stopPropagation();

            const previewDbFile = toPreviewableDbFile(
              record.dbFile,
              Number(projectId)
            );

            setFileToView(previewDbFile, {
              marginTop: FileViewerMargins.TABS,
            });
          };

          const handleFileSVFClick = (e?: any) => {
            e?.stopPropagation?.();

            if (!record.dbFile) {
              message.error(intl.formatMessage({ id: 'file-preview-error' }));
              return;
            }

            if (canOpenSVFViewer) {
              const unassignedTenderSVFFile = getUnassignedTenderSVFFile(
                record
              );

              if (!unassignedTenderSVFFile) {
                message.error(intl.formatMessage({ id: 'file-preview-error' }));
                return;
              }

              setFileToView(unassignedTenderSVFFile as any, {
                marginTop: FileViewerMargins.TABS,
              });

              return;
            }

            // тут поки нічого не робимо для convert/progress state,
            // але іконка може вже відображатись коректно по стейту
          };

          const renderMainFileIcon = () => {
            if (isCadFile) {
              return (
                <SVFIconType
                  key={`cad-file-icon-${record.id}`}
                  version={cadDisplayVersion}
                  extension={record.extension}
                  variant="table"
                  customTooltip={intl.formatMessage(
                    { id: type.icon },
                    { type: record.extension?.toUpperCase() }
                  )}
                  onClick={() => {
                    // icon only
                  }}
                  type={type}
                />
              );
            }

            return (
              <IconButton
                iconStyle={type.iconStyle}
                icon={type.icon}
                iconSecond={
                  record.state === DB_STATE.REMOVED ? ['fad', 'trash'] : null
                }
                colorSecondIcon={'red'}
                customStyleSecondIcon={{ left: '12px', top: '10px' }}
                color={type.color}
                customStyle={styles.icons}
                tooltip={intl.formatMessage(
                  { id: type.icon },
                  { type: record.extension?.toUpperCase() }
                )}
                badge={
                  record.state !== DB_STATE.REMOVED && type.is_empty && '0'
                }
                backgroundOff
              />
            );
          };

          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: isTenderFolder ? (
              <Tooltip
                placement="bottom"
                title={intl.formatMessage({
                  id: 'folder',
                })}>
                <div css={styles.outerIconWrapper}>
                  <div css={styles.iconContainer}>
                    <NavIcon
                      customStyle={{ marginLeft: '32px' }}
                      icon={FolderTender}
                    />
                  </div>
                </div>
              </Tooltip>
            ) : (
              <div css={styles.outerIconWrapper}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}>
                    {isPreviewableFormat && (
                      <div css={styles.iconContainer}>
                        <IconButton
                          click={handlePreviewClick}
                          iconStyle={type.iconStyle}
                          icon={['far', 'eye']}
                          color="red"
                        />
                      </div>
                    )}

                    {canShowSVFActionIcon && (
                      <SVFIconType
                        key={`svf-file-${record.id}-${fileState ?? 'none'}`}
                        version={fileVersion as any}
                        extension={record.extension}
                        variant="sidebar"
                        disabled={false}
                        customTooltip={intl.formatMessage({
                          id: 'svf-view-file-window',
                        })}
                        onClick={handleFileSVFClick}
                        type={type}
                      />
                    )}

                    {renderMainFileIcon()}
                  </div>
                </div>
              </div>
            ),
          };
        },
        sorter: sortByType,
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        width: 180,
        render: (name, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Fragment>
                {name}
                {record.favourite && (
                  <IconButton
                    icon={['fas', 'star']}
                    color="yellow"
                    backgroundOff
                  />
                )}
              </Fragment>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        width: 175,
        sorter: (a, b) =>
          (a.description || '').localeCompare(b.description || ''),
        ellipsis: true,
        render: (node, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: node,
          };
        },
      },
      // {
      //   title: intl.formatMessage({ id: 'revision' }),
      //   dataIndex: 'dbFile',
      //   sorter: (a, b) => sortByRevision(a, b),
      //   width: 175,
      //   ellipsis: true,
      //   render: (x, record) => {
      //     return {
      //       props: {
      //         style: getBackgroundRow(record),
      //       },
      //       children:
      //               fileVersionsContext[record.id]?.version === null
      //                 ? ''
      //                 : fileVersionsContext[record.id]?.version ??
      //                   record.dbFile?.files?.[record.dbFile.files.length - 1]?.serial_number ??
      //                   record.dbFile?.versions?.[record.dbFile.versions.length - 1]?.version ??
      //                   record.dbFile?.versions?.[0]?.version ??
      //                   '',
      //           };
      //   },
      // },
      {
        title: intl.formatMessage({ id: 'ver' }),
        dataIndex: 'dbFile',
        width: 80,
        align: 'center',
        onCell: () => ({ style: { padding: '4px 10px' } }),
        render: (file: any, element: any) => {
          let passFile = file;
          if (
            file &&
            (!file.versions || !file.versions.length) &&
            file.files &&
            file.files.length
          ) {
            passFile = {
              ...file,
              versions: file.files
                .map((f: any) => ({
                  ...f,
                  revision: f.serial_number ?? f.version ?? f.revision,
                  version: f.serial_number ?? f.version ?? f.revision,
                  id:
                    f.file_instance_id ||
                    f.file_id ||
                    f.registration_instance_id ||
                    f.id,
                }))
                .sort(
                  (a: any, b: any) => (b.revision || 0) - (a.revision || 0)
                ),
            };
          }
          return {
            props: {
              style: getBackgroundRow(element),
            },
            children: passFile && <VersionSelect file={passFile} />,
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'modified-date' }),

        dataIndex: 'updatedAt',
        sorter: sortByModified(fileVersionsContext),

        width: 140,
        align: 'center',
        render: (updatedAt, record) => {
          let modified = updatedAt;

          // if file, show "modified" of selected version
          if (record.dbFile?.versions || record.dbFile?.files) {
            modified =
              fileVersionsContext[record.id]?.updated_at ||
              record.dbFile?.files?.[record.dbFile?.files.length - 1]
                ?.updated_at ||
              record.dbFile?.versions?.[record.dbFile.versions.length - 1]
                ?.updated_at ||
              record.dbFile?.versions?.[0]?.updated_at ||
              updatedAt;
          }
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: formatDate(modified, 'short'),
          };
        },
      },
    ],
    [
      fileVersionsContext,
      intl,
      styles.icons,
      styles.small,
      focusOptions,
      highlightedRow,
      getBackgroundRow,
      sortByRevision,
    ]
  );

  // disable selection (cherrypicker) if no RIGHTS_LEVEL.OPEN on current folder
  const selectionDisabled = permissionLevel < RIGHTS_LEVEL.OPEN;

  /** calculate !getCheckboxProps.disabled */
  const isRecordSelectDisabled = (record: TableEntry) => {
    if (window.sessionStorage.getItem('appState') === 'superUser') {
      return false;
    }

    // if root, use folder permission
    if (folderId === null && record.dbFolder) {
      return record.dbFolder.permission < RIGHTS_LEVEL.OPEN;
    }
    if (suMode && record.state === DB_STATE.REMOVED) {
      return false;
    }

    return true || selectionDisabled || record.disabled;
  };

  const handleSelectShift = (record: TableEntry, index?: number) => {
    if (selectedKeys && selectedKeys.length === 0) {
      return setSelectedItems((prevSelectedItems) => [
        record,
        ...prevSelectedItems,
      ]);
    }
    if (selectedKeys && selectedKeys.length > 0) {
      const selectedIndex = selectedKeys.findIndex(
        (element) => element === record.key
      );
      if (selectedIndex >= 0) {
        return setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.key !== record.key)
        );
      }
      const tableKeys = getTableData().map((item) => item.key);

      const isAnySelectedInCurrentView = selectedKeys.some((el) =>
        tableKeys.includes(el as string)
      );
      if (!isAnySelectedInCurrentView) {
        return setSelectedItems((prevSelectedItems) => [
          record,
          ...prevSelectedItems,
        ]);
      }

      const lastIndexReversed = getTableData()
        .reverse()
        .findIndex((el) => selectedKeys.includes(el.key));

      const lastIndex = getTableData().findIndex(
        (el) => el.key === getTableData().reverse()[lastIndexReversed].key
      );
      const firstIndex = getTableData().findIndex((el) =>
        selectedKeys.includes(el.key)
      );

      if ((index as number) >= 0) {
        if ((index as number) < firstIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(index, firstIndex),
          ]);
        }

        if ((index as number) > lastIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(lastIndex + 1, (index as number) + 1),
          ]);
        }

        if ((index as number) > firstIndex && (index as number) < lastIndex) {
          return setSelectedItems((prevState) =>
            uniq([
              ...prevState,
              ...getTableData().slice(firstIndex, (index as number) + 1),
            ])
          );
        }
      }
    }
  };

  const getTableData = () => {
    return [...filteredFolders, ...filteredFiles];
  };

  const content = (
    <Fragment>
      <Table<TableEntry>
        css={[
          styles.table,
          // tableProps.scroll?.y && {
          //   '.ant-table-body': {
          //     height: tableProps.scroll.y,
          //   },
          // },
        ]}
        showSorterTooltip={false}
        className={`main-content-window ${
          window.sessionStorage.getItem('appState') === 'timeMachine' &&
          'timeMachine-table'
        } ${suMode && 'su-table'}`}
        pagination={false}
        bordered
        columns={columns}
        dataSource={[...filteredFolders, ...filteredFiles]}
        loading={
          filesQuery?.isLoading || foldersQuery?.isLoading
          // ||
          //foldersQuery?.isIdle
        }
        rowClassName={(record) => {
          let className = suMode ? 'su-row-table ' : '';
          if (window.sessionStorage.getItem('appState') === 'timeMachine') {
            className += 'timeMachine-row-table' + ' ';
          }
          if (record.key === highlightedRow?.key) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (
            record.key === highlightedRow?.key &&
            record.dbFolder &&
            !record.dbFolder.focus
          ) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (record.disabled) {
            className += 'unfinished' + ' ';
          }

          return className;
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedKeys,
          columnWidth: 50,
          renderCell: (type, record, element, node) => {
            return {
              props: {
                style: getBackgroundRow(record),
              },
              children: node,
            };
          },
          onSelect,
          onSelectAll,
          getCheckboxProps: (record) => ({
            disabled: isRecordSelectDisabled(record),
          }),
        }}
        onRow={(record, index) => ({
          ...handleRow(record, index),
          onClick: (event) => {
            if (event.shiftKey) {
              return handleSelectShift(record, index);
            }
            const onClickAction = handleRow(record, index).onClick;
            if (onClickAction) {
              return onClickAction(event);
            }
          },
        })}
        {...tableProps}
      />
      <div
        onContextMenu={() => {
          onEmptyOverlay && onEmptyOverlay();
        }}
        className="test-class"
        style={{ height: 'inherit', width: '100%' }}></div>
    </Fragment>
  );

  if (fileToView) {
    return (
      <div
        style={{
          height: tableProps.scroll && tableProps.scroll.y,
          maxHeight: tableProps.scroll && tableProps.scroll.y,
          display: 'flex',
          flexDirection: 'column',
          width: '1230px',
        }}>
        {content}
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
      }}>
      <div
        style={{
          minWidth: 1150,
          // width: 1065,
          height: tableProps.scroll && `calc(${tableProps.scroll.y} + 10px)`,
          maxHeight: tableProps.scroll && `calc(${tableProps.scroll.y} + 10px)`,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}>
        {content}
      </div>
    </div>
  );
};
